import type { CSSProperties, FC, MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import { currencyFormatter, type PricingVisibleSection, type WebTheme } from '@shared/dream-components';
import cx from 'classnames';

import Pricing from '~/components/DreamBuilder/nodes/Pricing';
import { useUpgradeContext } from '~/context/useUpgradeContext';
import useEmail from '~/hooks/useEmail';
import { PriceInterval } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';
import type { loader as communityLoader } from '~/routes/community';

import type { CommunityPurchasableTiers } from '../../../_hooks/useCommunityPurchasableTiers';
import { useUpgradePricingNode } from '../useUpgradePricingNode';

interface PricingStepProps {
  /** Pricing reports no tier on success, so the step reads the selection from the upgrade context and forwards it. */
  onSuccess: (ssoLoginUrl?: string, tierId?: string) => void;
  visibleSection: PricingVisibleSection;
  pricing: CommunityPurchasableTiers;
  successRedirectTo: string;
}

export const PricingStep: FC<PricingStepProps> = ({ onSuccess, visibleSection, pricing, successRedirectTo }) => {
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const selectedTierIdRef = useRef<string | undefined>(undefined);
  const [checkoutCta, setCheckoutCta] = useState<string | undefined>(undefined);
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const community = layoutData?.community;
  const [storedEmail, setStoredEmail] = useEmail();
  const subscriptionEmail = rootData?.subscription?.email;

  // The internal checkout reads the email only from useEmail (URL/localStorage),
  // never the logged-in subscription — seed it or logged-in buyers can't pay.
  useEffect(() => {
    if (subscriptionEmail && storedEmail !== subscriptionEmail) {
      setStoredEmail(subscriptionEmail);
    }
  }, [subscriptionEmail, storedEmail, setStoredEmail]);

  const { purchasableTiers, isError } = pricing;

  const theme: WebTheme | undefined = useMemo(() => {
    const baseTheme = rootData?.site.primary_theme;
    if (!baseTheme) return undefined;
    if (!community) return baseTheme;

    return {
      ...baseTheme,
      primary_color: community.primary_color,
      background_color: community.background_color,
      text_on_background_color: community.text_on_background_color,
      text_on_primary_color: community.text_on_primary_color,
    };
  }, [rootData?.site.primary_theme, community]);

  const pricingNode = useUpgradePricingNode({ tiers: purchasableTiers ?? [], theme, checkoutCta });

  if (purchasableTiers === null && !isError) {
    return (
      <div className="flex w-full justify-center py-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  // Loaded-but-empty or failed: the modal renders the closed-community message
  // instead of mounting this step; this guard only covers render races.
  if (isError || !pricingNode) {
    return null;
  }

  return (
    <div
      className={cx(
        'flex w-full justify-center',
        // apply community styles to inner PricingElement elements
        '[&_input[type=email]]:h-12 [&_input[type=email]]:font-medium [&_input[type=email]]:!text-[16px] sm:[&_input[type=email]]:!text-[13px]',
        '[&_input[type=submit]]:h-12 [&_input[type=submit]]:!rounded-full [&_input[type=submit]]:px-5 [&_input[type=submit]]:!text-[13px] [&_input[type=submit]]:font-medium',
        // same outline as PillButton primary, so a primary color close to the modal background still reads as a button
        '[&_input[type=submit]]:!border [&_input[type=submit]]:!border-solid [&_input[type=submit]]:!border-community-text-on-primary/10 [&_input[type=submit]]:shadow-community-button',
        '[&_input[type=submit]:disabled]:!bg-[color:var(--button-color)] [&_input[type=submit]:disabled]:!opacity-60 [&_input[type=submit]:disabled]:cursor-not-allowed',
      )}
      style={
        {
          '--input-placeholder-color': 'rgba(0, 0, 0, 0.3)',
          '--input-background-color': 'rgba(0, 0, 0, 0.05)',
        } as CSSProperties
      }
    >
      <Pricing
        node={pricingNode}
        onSuccess={(ssoLoginUrl) => onSuccess(ssoLoginUrl, selectedTierIdRef.current)}
        successRedirectTo={successRedirectTo}
        forceStripePaymentElement
        visibleSection={visibleSection}
      >
        <SelectionReporter tierIdRef={selectedTierIdRef} onCheckoutCtaChange={setCheckoutCta} />
        {visibleSection === 'checkout' && <RecurringTerms />}
      </Pricing>
    </div>
  );
};

// Rendered inside Pricing, the only place the upgrade context is readable from.
const SelectionReporter: FC<{
  tierIdRef: MutableRefObject<string | undefined>;
  onCheckoutCtaChange: (cta: string | undefined) => void;
}> = ({ tierIdRef, onCheckoutCtaChange }) => {
  const { selectedTier, selectedPrice } = useUpgradeContext();
  const selectedTierId = selectedTier?.id;
  const selectedPriceCta = selectedPrice?.cta || undefined;

  useEffect(() => {
    tierIdRef.current = selectedTierId;
  }, [tierIdRef, selectedTierId]);

  useEffect(() => {
    onCheckoutCtaChange(selectedPriceCta);
  }, [onCheckoutCtaChange, selectedPriceCta]);

  return null;
};

const RECURRING_INTERVALS: ReadonlySet<string> = new Set([
  PriceInterval.MONTH,
  PriceInterval.QUARTER,
  PriceInterval.YEAR,
]);

// Mirrors the renewal disclosure of Stripe's hosted checkout. An offer changes what the first
// charges cost and the recap above states them, so with an offer only the renewal is disclosed.
const RecurringTerms: FC = () => {
  const { t } = useTranslation();
  const { selectedPrice } = useUpgradeContext();
  const premiumOffer = selectedPrice?.premium_offer;

  if (!selectedPrice || !RECURRING_INTERVALS.has(selectedPrice.interval)) {
    return null;
  }

  if (premiumOffer && !premiumOffer.card_required) {
    return null;
  }

  const terms = premiumOffer
    ? t('community.upgrade_modal.recurring_terms_with_offer')
    : t('community.upgrade_modal.recurring_terms', {
        price: currencyFormatter({ value: selectedPrice.amount_cents / 100, currency: selectedPrice.currency }),
        interval: t(`billing.summary.intervals.${selectedPrice.interval}`),
      });

  return <p className="mt-3 text-center text-xs leading-4 text-black/60">{terms}</p>;
};
